import { faWarning } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { round } from "@repo/system";
import { T, useT } from "@repo/transifex";
import { Button, Dialog } from "~/components";
import type { DiscountTypes } from "~/components/Inputs/DiscountInput";
import { DiscountInput } from "~/components/Inputs/DiscountInput";
import { basketActions } from "~/providers/store/basket";
import { useOrderDiscount } from "~/providers/store/basket/hooks/use-basket";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { useBasketContext } from "~/providers/store/basket/context";

import {
  FIXED_PERCENTAGE_DISCOUNTS,
  FixedPercentageDiscounts
} from "./fixed-percentage-discounts";

export const DiscountOrderPage = () => {
  const unitPriceSubTotalInclVat = useBasketContext(
    (state) => state.sum.unitPriceSubTotalInclVat
  );
  const orderDiscountableTotalInclVat = useBasketContext(
    (state) => state.sum.orderDiscountableTotalInclVat
  );
  const { currency } = useCurrentTillLocalisation();
  const t = useT();

  const navigate = useNavigate();

  const orderDiscount = useOrderDiscount();

  const [discountType, setDiscountType] = useState<DiscountTypes>(
    orderDiscount.type
  );

  const [discount, setDiscount] = useState<number | undefined>(
    orderDiscount.type === "currency"
      ? orderDiscount.amount / 100
      : (unitPriceSubTotalInclVat / 100) * (orderDiscount.amount / 100)
  );

  const [percentage, setPercentage] = useState<number | undefined>(
    orderDiscount.type === "percentage"
      ? orderDiscount.amount
      : (orderDiscount.amount / unitPriceSubTotalInclVat) * 100
  );

  const handleChangeDiscount = (newDiscount: number) => {
    if (newDiscount === 0) {
      setDiscount(undefined);
      setPercentage(undefined);
      return;
    }
    const percentage = round(
      (newDiscount / (unitPriceSubTotalInclVat / 100)) * 100,
      2
    );

    setDiscount(newDiscount);
    setPercentage(percentage);
  };

  const handleChangePercentage = (newPercentage: number) => {
    if (newPercentage === 0) {
      setDiscount(undefined);
      setPercentage(undefined);
      return;
    }

    const discount = round(
      (newPercentage * (unitPriceSubTotalInclVat / 100)) / 100,
      2
    );

    setDiscount(discount);
    setPercentage(newPercentage);
  };

  const unitDiscount = round((discount ?? 0) * 100, 0);

  const isAboveOrder =
    discountType === "currency"
      ? unitDiscount > round(orderDiscountableTotalInclVat, 0)
      : (percentage ?? 0) > 100;

  const discountError = isAboveOrder ? t("Maximum discount is 100%") : null;

  return (
    <Dialog className="p-5" size="lg">
      <div className="flex w-full flex-col items-center gap-4">
        <div className="relative mb-4 flex w-full items-center justify-center">
          <Link className="absolute left-0" to="..">
            <Button className="text-danger-dark" variant="transparent">
              <T _str="Cancel" />
            </Button>
          </Link>

          <h1 className="text-2xl font-bold">
            <T _str="Order discount" />
          </h1>
        </div>
        <div className="flex w-full flex-col">
          <div className="flex h-16 w-full items-center justify-between">
            <p className="font-semibold">
              <T _str="Discount" />
            </p>

            {discountType === "percentage" && (
              <FixedPercentageDiscounts>
                {FIXED_PERCENTAGE_DISCOUNTS.map((percentage) => (
                  <FixedPercentageDiscounts.Button
                    key={percentage}
                    onClick={() => {
                      setPercentage(percentage);
                    }}
                  >
                    {percentage}%
                  </FixedPercentageDiscounts.Button>
                ))}
              </FixedPercentageDiscounts>
            )}
          </div>

          <DiscountInput
            currency={currency}
            handleChangeDiscount={
              discountType === "currency"
                ? handleChangeDiscount
                : handleChangePercentage
            }
            handleChangeType={setDiscountType}
            type={discountType}
            value={discountType === "currency" ? discount : percentage}
          />
          {discountError ? (
            <div className="mt-3 flex items-center gap-1 text-warning-dark">
              <FontAwesomeIcon icon={faWarning} />
              <span className="text-base font-medium">{discountError}</span>
            </div>
          ) : null}

          <Button
            className="mt-8"
            disabled={Boolean(discountError)}
            onClick={() => {
              if (discount === undefined || percentage === undefined) {
                basketActions.setOrderDiscount({
                  amount: 0,
                  type: discountType
                });
              } else {
                basketActions.setOrderDiscount({
                  amount:
                    discountType === "currency" ? unitDiscount : percentage,
                  type: discountType
                });
              }
              navigate("..");
            }}
            size="large"
            variant="primary"
          >
            <T _str="Confirm" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
